(() => {
  const STORAGE_KEY = 'driplocker:last-added-product-title';
  const PREDICTIVE_DELAY = 220;
  const instances = new WeakMap();


  // Prestige opens cart-drawer automatically on cart:change when baseEvent is "variant:add".
  // On all screen sizes we keep native cart rendering/count updates, but rename that one event
  // before it reaches Prestige's bubbling cart-drawer listener so our slim bag bar is shown instead.
  document.addEventListener('cart:change', (event) => {
    if (event.detail?.baseEvent !== 'variant:add') return;
    event.detail.baseEvent = 'driplocker:variant:add';
  }, true);

  class DriplockerMobileHeader {
    constructor(root) {
      this.root = root;
      this.header = root.closest('x-header.header');
      this.searchOpenButton = root.querySelector('[data-driplocker-search-open]');
      this.searchForm = root.querySelector('[data-driplocker-search-form]');
      this.searchInput = root.querySelector('[data-driplocker-search-input]');
      this.searchCloseButton = root.querySelector('[data-driplocker-search-close]');
      this.cartCountWrap = root.querySelector('[data-driplocker-cart-count-wrap]');
      this.bagBar = root.querySelector('[data-driplocker-bag-bar]');
      this.bagTitle = root.querySelector('[data-driplocker-bag-title]');
      this.predictivePanel = root.querySelector('[data-driplocker-predictive-panel]');
      this.predictiveResults = root.querySelector('[data-driplocker-predictive-results]');
      this.pageType = root.dataset.pageType;
      this.initialCartCount = Number.parseInt(root.dataset.cartCount || '0', 10) || 0;
      this.initialCartTitle = root.dataset.initialCartTitle || '';
      this.rafId = null;
      this.predictiveTimer = null;
      this.predictiveAbortController = null;
      this.abortController = new AbortController();

      this.bindEvents();
      this.restoreBagBar();
      this.updateCartCountVisibility(this.initialCartCount);
      this.updateHeroState();
    }

    bindEvents() {
      const { signal } = this.abortController;

      this.searchOpenButton?.addEventListener('click', () => this.openSearch(), { signal });
      this.searchCloseButton?.addEventListener('click', () => this.closeSearch(), { signal });

      this.searchInput?.addEventListener('input', () => this.schedulePredictiveSearch(), { signal });

      this.searchForm?.addEventListener('submit', (event) => {
        if (!this.searchInput?.value.trim()) {
          event.preventDefault();
          this.searchInput?.focus();
        }
      }, { signal });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.searchForm?.hidden) {
          event.preventDefault();
          this.closeSearch();
        }
      }, { signal });

      document.addEventListener('variant:add', (event) => {
        const items = event.detail?.items || [];
        const item = items[0];
        const title = item?.product_title || item?.title;

        if (title) {
          this.showBagBar(title, true);
        }
      }, { signal });

      document.addEventListener('cart:change', (event) => {
        const count = Number(event.detail?.cart?.item_count || 0);
        this.updateCartCountVisibility(count);

        if (count === 0) {
          this.hideBagBar();
        }
      }, { signal });

      document.addEventListener('cart:refresh', () => this.refreshCartState(), { signal });
      window.addEventListener('pageshow', () => this.refreshCartState(), { signal });

      window.addEventListener('scroll', () => this.scheduleHeroStateUpdate(), { passive: true, signal });
      window.addEventListener('resize', () => this.scheduleHeroStateUpdate(), { passive: true, signal });
    }

    openSearch() {
      if (!this.searchForm || !this.searchOpenButton) return;

      this.searchOpenButton.closest('[data-driplocker-actions]')?.setAttribute('hidden', '');
      this.searchForm.hidden = false;
      this.root.classList.add('is-search-open');
      this.searchOpenButton.setAttribute('aria-expanded', 'true');
      this.scheduleHeroStateUpdate();

      requestAnimationFrame(() => this.searchInput?.focus({ preventScroll: true }));
    }

    closeSearch() {
      if (!this.searchForm || !this.searchOpenButton) return;

      this.cancelPredictiveSearch();
      this.hidePredictivePanel();
      this.searchForm.hidden = true;
      this.root.classList.remove('is-search-open');
      this.searchOpenButton.closest('[data-driplocker-actions]')?.removeAttribute('hidden');
      this.searchOpenButton.setAttribute('aria-expanded', 'false');
      this.scheduleHeroStateUpdate();
      this.searchOpenButton.focus({ preventScroll: true });
    }

    schedulePredictiveSearch() {
      const query = this.searchInput?.value.trim() || '';

      window.clearTimeout(this.predictiveTimer);
      this.cancelPredictiveRequest();

      if (!query) {
        this.hidePredictivePanel();
        return;
      }

      this.predictiveTimer = window.setTimeout(() => {
        this.fetchPredictiveResults(query);
      }, PREDICTIVE_DELAY);
    }

    async fetchPredictiveResults(query) {
      if (!this.predictivePanel || !this.predictiveResults || !query) return;

      this.predictiveAbortController = new AbortController();
      this.predictivePanel.hidden = false;
      this.predictivePanel.setAttribute('aria-busy', 'true');

      const rootPath = window.Shopify?.routes?.root || '/';
      const url = new URL(`${window.location.origin}${rootPath}search/suggest.json`);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '4');
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[options][unavailable_products]', 'last');

      try {
        const response = await fetch(url.toString(), {
          headers: { Accept: 'application/json' },
          signal: this.predictiveAbortController.signal
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Predictive search failed: ${response.status} ${errorText}`);
        }

        const data = await response.json();
        const products = data?.resources?.results?.products || [];

        this.renderPredictiveProducts(products);
        this.predictivePanel.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('[DRIPLOCKER] Predictive search unavailable', error);
          this.hidePredictivePanel();
        }
      } finally {
        this.predictivePanel?.setAttribute('aria-busy', 'false');
      }
    }

    renderPredictiveProducts(products) {
      if (!this.predictiveResults) return;

      this.predictiveResults.replaceChildren();

      if (!Array.isArray(products) || products.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'driplocker-predictive-empty';
        empty.textContent = 'NO RESULTS';
        this.predictiveResults.append(empty);
        return;
      }

      const list = document.createElement('div');
      list.className = 'driplocker-predictive-products';
      list.setAttribute('role', 'list');

      products.slice(0, 4).forEach((product) => {
        const link = document.createElement('a');
        link.className = 'driplocker-predictive-product';
        link.href = product.url || '#';
        link.setAttribute('role', 'listitem');

        const imageWrap = document.createElement('span');
        imageWrap.className = 'driplocker-predictive-product__image';

        const imageUrl = product.featured_image?.url || product.image || '';
        if (imageUrl) {
          const image = document.createElement('img');
          image.src = imageUrl;
          image.alt = product.featured_image?.alt || product.title || '';
          image.loading = 'lazy';
          image.decoding = 'async';
          imageWrap.append(image);
        }

        const meta = document.createElement('span');
        meta.className = 'driplocker-predictive-product__meta';

        const title = document.createElement('span');
        title.className = 'driplocker-predictive-product__title';
        title.textContent = product.title || '';

        const price = document.createElement('span');
        price.className = 'driplocker-predictive-product__price';
        price.textContent = this.formatPredictivePrice(product.price);

        meta.append(title);
        if (price.textContent) meta.append(price);
        link.append(imageWrap, meta);
        list.append(link);
      });

      this.predictiveResults.append(list);
    }

    formatPredictivePrice(value) {
      const numericValue = Number.parseFloat(value);
      if (!Number.isFinite(numericValue)) return '';

      try {
        return new Intl.NumberFormat(window.Shopify?.locale || document.documentElement.lang || 'en', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 2
        }).format(numericValue);
      } catch (_) {
        const cleanValue = Number.isInteger(numericValue) ? numericValue.toFixed(0) : numericValue.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
        return `€${cleanValue}`;
      }
    }

    cancelPredictiveRequest() {
      this.predictiveAbortController?.abort();
      this.predictiveAbortController = null;
    }

    cancelPredictiveSearch() {
      window.clearTimeout(this.predictiveTimer);
      this.predictiveTimer = null;
      this.cancelPredictiveRequest();
    }

    hidePredictivePanel() {
      if (this.predictivePanel) {
        this.predictivePanel.hidden = true;
        this.predictivePanel.setAttribute('aria-busy', 'false');
      }
      if (this.predictiveResults) this.predictiveResults.innerHTML = '';
    }

    showBagBar(title, pulse = false) {
      if (!this.bagBar || !this.bagTitle) return;

      const cleanTitle = String(title).trim();
      if (!cleanTitle) return;

      this.bagTitle.textContent = `${cleanTitle.toUpperCase()} ADDED TO BAG →`;
      this.bagBar.hidden = false;

      if (pulse) {
        this.bagBar.classList.remove('is-add-pulsing');
        // Force a reflow so repeated add-to-cart actions replay the animation.
        void this.bagBar.offsetWidth;
        this.bagBar.classList.add('is-add-pulsing');
        this.bagBar.addEventListener('animationend', () => {
          this.bagBar?.classList.remove('is-add-pulsing');
        }, { once: true });
      }

      this.scheduleHeroStateUpdate();

      try {
        sessionStorage.setItem(STORAGE_KEY, cleanTitle);
      } catch (_) {}
    }

    hideBagBar() {
      if (this.bagBar) this.bagBar.hidden = true;
      if (this.bagTitle) this.bagTitle.textContent = '';
      this.scheduleHeroStateUpdate();

      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (_) {}
    }

    restoreBagBar() {
      if (this.initialCartCount <= 0) {
        this.hideBagBar();
        return;
      }

      let storedTitle = '';
      try {
        storedTitle = sessionStorage.getItem(STORAGE_KEY) || '';
      } catch (_) {}

      const title = storedTitle || this.initialCartTitle;
      if (title) this.showBagBar(title);
    }

    updateCartCountVisibility(count) {
      if (!this.cartCountWrap) return;
      this.cartCountWrap.hidden = count <= 0;
    }

    async refreshCartState() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
          headers: { Accept: 'application/json' }
        });
        if (!response.ok) return;

        const cart = await response.json();
        this.updateCartCountVisibility(Number(cart.item_count || 0));

        if (Number(cart.item_count || 0) === 0) {
          this.hideBagBar();
        } else if (this.bagBar?.hidden) {
          const firstTitle = cart.items?.[0]?.product_title || cart.items?.[0]?.title || this.initialCartTitle;
          if (firstTitle) this.showBagBar(firstTitle);
        }
      } catch (_) {
        // Native Prestige cart logic remains the source of truth if this optional refresh fails.
      }
    }

    scheduleHeroStateUpdate() {
      if (this.rafId !== null) return;

      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.updateHeroState();
      });
    }

    updateHeroState() {
      if (!this.header) return;

      // Search and the persistent add-to-bag state always use a solid header.
      // This fixes the white-on-transparent state when returning to the homepage with items in the bag.
      if (!this.searchForm?.hidden || !this.bagBar?.hidden) {
        this.header.dataset.driplockerState = 'solid';
        return;
      }

      const headerRow = this.root.querySelector('.driplocker-mobile-header__row');
      const headerRect = headerRow?.getBoundingClientRect() || this.header.getBoundingClientRect();
      const sampleX = Math.max(1, Math.min(window.innerWidth - 1, window.innerWidth / 2));
      const sampleY = Math.max(1, Math.min(window.innerHeight - 1, headerRect.bottom + 1));

      const mediaIsUnderHeader = document.elementsFromPoint(sampleX, sampleY).some((element) => {
        if (this.header.contains(element) || element.closest('.shopify-section--header')) return false;

        const mediaSurface = element.closest(
          '[data-driplocker-transparent-media], [allow-transparent-header], .content-over-media, .ec-section, .shopify-section--video, .shopify-section--slideshow'
        );

        if (!mediaSurface) return false;

        // Product imagery is intentionally excluded: only editorial image/video
        // surfaces should receive the transparent header treatment.
        return !mediaSurface.closest('.product-card, .product-gallery');
      });

      this.header.dataset.driplockerState = mediaIsUnderHeader ? 'transparent' : 'solid';
    }

    destroy() {
      this.cancelPredictiveSearch();
      this.abortController.abort();
      if (this.rafId !== null) cancelAnimationFrame(this.rafId);
      instances.delete(this.root);
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll?.('[data-driplocker-mobile-header]').forEach((root) => {
      if (instances.has(root)) return;
      instances.set(root, new DriplockerMobileHeader(root));
    });
  };

  init();

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll?.('[data-driplocker-mobile-header]').forEach((root) => {
      instances.get(root)?.destroy();
    });
  });
})();
